import { WebViewerInstance } from '@pdftron/webviewer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router';
import { Loading } from 'components/atoms/loading';
import {
  AttachmentDiscussion,
  FloatingComponent,
  FloatingForm,
  useImagePreview,
} from 'components/organisms/image-preview';
import { ComparisonFooter } from 'components/organisms/image-preview/comparison-footer';
import {
  ANNOTATION_STICKY_MODE,
  WORKSPACE_ID,
} from 'components/organisms/image-preview/constants';
import { ComparisionPreviewHeader } from 'components/organisms/image-preview/preview-header/comparision-preview-header';
import { PreviewToolbar } from 'components/organisms/image-preview/preview-toolbar';
import { ReactCompareSlider } from 'components/organisms/react-compare-slider';
import { RoutePaths } from 'constant';
import { useAttachmentDetails } from '../use-attachment-details';
import { WorkspaceWrapper } from '../workspace';
import { ComparisonComments } from './comparison-comments';
import {
  OverlayWrapper,
  Wrapper,
  ComaparisonModeWrapper,
  CommentsSection,
  LoadingContainer,
  StyledDiv,
} from './comparison-styled-components';

export const OverlayComparison = () => {
  const params = useLocation();
  const match = useRouteMatch([RoutePaths.attachmentWorkspaceOverlay]);
  const { attachmentId, compareAttachmentId } = match?.params as {
    attachmentId: number;
    compareAttachmentId: number;
  };
  const {
    latestVersion,
    versionFileList,
    leftAttachment,
    setLeftAttachment,
    rightAttachment,
    setRightAttachment,
    isChatSectionComparisonVisible,
    setChatSectionComparisonVisiblity,
    onOpenParentTask,
  } = useAttachmentDetails(attachmentId, compareAttachmentId);
  const [isAttachmentLinked, setIsAttachmentLinked] = useState(false);
  const [selectedInsatnce, setSelectedInstance] =
    useState<WebViewerInstance | null>(null);
  const [discussionAttachmentId, setDiscussionAttachmentId] = useState(
    leftAttachment?.attachmentId,
  );
  const [activeToolOverlay, setActiveToolOverlay] = useState(
    ANNOTATION_STICKY_MODE,
  );
  const [showResolvedCommentsLeft, setShowResolvedCommentsLeft] =
    useState(false);
  const [showResolvedComments, setShowResolvedComments] = useState(false);
  const [annotationVisiblity, setAnnotationVisiblity] = useState(true);
  const toolFromRouter = new URLSearchParams(params.search).get('tool');

  const {
    instance: instanceLeft,
    ref: refLeft,
    addNewCommentFormVisiblity: addNewCommentFormVisiblityLeft,
    floatingFormCoordinates: floatingFormCoordinatesLeft,
    activeDiscussion: activeDiscussionLeft,
    setAddNewCommentFormVisiblity: setAddNewCommentFormVisiblityLeft,
    filteredDicsussions: filteredDicsussionsLeft,
    discussions: discussionsLeft,
    activeAnnotation: activeAnnotationLeft,
    onSubmit: onSubmitLeft,
    isPrivate: isPrivateLeft,
    onPrivateButtonClick: onPrivateButtonClickLeft,
    setActiveAnnotation: setActiveAnnotationLeft,
    setActiveDiscussion: setActiveDiscussionLeft,
    setFloatingFormCoordinates: setFloatingFormCoordinatesLeft,
  } = useImagePreview({
    data: leftAttachment,
    src: leftAttachment?.location,
    attachmentId: leftAttachment?.attachmentId,
    activeToolOverlay,
    setSelectedInstance,
    setDiscussionAttachmentId,
    setActiveToolOverlay,
    previewConfig: {
      zoomTogether: true,
      rotateTogether: true,
      scrollTogether: true,
      isOverlay: true,
    },
    annotationVisiblity,
    showResolvedComments: showResolvedCommentsLeft,
    setShowResolvedComments: (value: boolean) =>
      setShowResolvedCommentsLeft(value),
  });

  const {
    instance,
    ref,
    addNewCommentFormVisiblity,
    floatingFormCoordinates,
    activeDiscussion,
    setAddNewCommentFormVisiblity,
    filteredDicsussions,
    discussions,
    activeAnnotation,
    onSubmit,
    isPrivate,
    onPrivateButtonClick,
    setActiveAnnotation,
    setActiveDiscussion,
    setFloatingFormCoordinates,
  } = useImagePreview({
    data: rightAttachment,
    src: rightAttachment?.location,
    attachmentId: rightAttachment?.attachmentId,
    activeToolOverlay,
    setSelectedInstance,
    setDiscussionAttachmentId,
    setActiveToolOverlay,
    previewConfig: {
      zoomTogether: true,
      rotateTogether: true,
      scrollTogether: true,
      isOverlay: true,
    },
    annotationVisiblity,
    showResolvedComments,
    setShowResolvedComments: (value: boolean) => setShowResolvedComments(value),
  });

  const onFloatingFormClose = useCallback(() => {
    if (discussionAttachmentId === Number(attachmentId)) {
      setAddNewCommentFormVisiblityLeft(false);
      const _disucssion = filteredDicsussionsLeft.find((d) =>
        d.comments.find(
          (comment) => comment.commentGuid === activeAnnotationLeft?.Id,
        ),
      );

      if (!_disucssion && activeAnnotationLeft) {
        instanceLeft.current?.Core.annotationManager.deleteAnnotation(
          activeAnnotationLeft,
        );
      }
    } else {
      setAddNewCommentFormVisiblity(false);
      const _disucssion = filteredDicsussions.find((d) =>
        d.comments.find(
          (comment) => comment.commentGuid === activeAnnotation?.Id,
        ),
      );

      if (!_disucssion && activeAnnotation) {
        instance.current?.Core.annotationManager.deleteAnnotation(
          activeAnnotation,
        );
      }
    }
  }, [
    activeAnnotation,
    activeAnnotationLeft,
    attachmentId,
    discussionAttachmentId,
    filteredDicsussions,
    filteredDicsussionsLeft,
    instance,
    instanceLeft,
    setAddNewCommentFormVisiblity,
    setAddNewCommentFormVisiblityLeft,
  ]);

  const isLeftAttachment = useMemo(
    () => discussionAttachmentId === Number(attachmentId),
    [attachmentId, discussionAttachmentId],
  );

  const showResolvedCommentsSelected = useMemo(
    () => (isLeftAttachment ? showResolvedCommentsLeft : showResolvedComments),
    [isLeftAttachment, showResolvedComments, showResolvedCommentsLeft],
  );

  useEffect(() => {
    if (toolFromRouter) {
      setActiveToolOverlay(toolFromRouter);
    }
  }, [toolFromRouter]);

  useEffect(() => {
    if (rightAttachment?.attachmentId) {
      setDiscussionAttachmentId(rightAttachment?.attachmentId);
    }
  }, [rightAttachment?.attachmentId, setDiscussionAttachmentId]);

  useEffect(() => {
    if (leftAttachment?.attachmentId) {
      setDiscussionAttachmentId(leftAttachment?.attachmentId);
    }
  }, [leftAttachment?.attachmentId, setDiscussionAttachmentId]);

  if (leftAttachment && rightAttachment && latestVersion) {
    return (
      <>
        {(instanceLeft.current &&
          addNewCommentFormVisiblityLeft &&
          floatingFormCoordinatesLeft.x > 0 &&
          floatingFormCoordinatesLeft.y > 0 &&
          !activeDiscussionLeft) ||
        (instance.current &&
          addNewCommentFormVisiblity &&
          floatingFormCoordinates.x > 0 &&
          floatingFormCoordinates.y > 0 &&
          !activeDiscussion) ? (
          <FloatingForm
            onClose={onFloatingFormClose}
            onSubmit={isLeftAttachment ? onSubmitLeft : onSubmit}
            projectId={
              isLeftAttachment
                ? leftAttachment.projectId
                : rightAttachment.projectId
            }
            y={
              isLeftAttachment
                ? floatingFormCoordinatesLeft.y
                : floatingFormCoordinates.y
            }
            x={
              isLeftAttachment
                ? floatingFormCoordinatesLeft.x
                : floatingFormCoordinates.x
            }
            isPrivate={isLeftAttachment ? isPrivateLeft : isPrivate}
            onPrivateBtnClick={
              isLeftAttachment ? onPrivateButtonClickLeft : onPrivateButtonClick
            }
          />
        ) : null}
        {discussionAttachmentId === Number(attachmentId) ? (
          instanceLeft.current &&
          activeDiscussionLeft &&
          floatingFormCoordinatesLeft.x > 0 &&
          floatingFormCoordinatesLeft.y > 0 ? (
            <FloatingComponent
              x={floatingFormCoordinatesLeft.x}
              y={floatingFormCoordinatesLeft.y}
            >
              <AttachmentDiscussion
                activeDiscussion={activeDiscussionLeft}
                attachmentId={attachmentId}
                discussion={activeDiscussionLeft}
                instance={instanceLeft.current}
                projectId={leftAttachment.projectId}
                setActiveAnnotation={setActiveAnnotationLeft}
                setActiveDiscussion={setActiveDiscussionLeft}
                setFloatingFormCoordinates={setFloatingFormCoordinatesLeft}
                onDiscussionClick={() =>
                  setActiveDiscussionLeft(activeDiscussionLeft)
                }
                showResolvedComments={showResolvedCommentsLeft}
              />
            </FloatingComponent>
          ) : null
        ) : instance.current &&
          activeDiscussion &&
          floatingFormCoordinates.x > 0 &&
          floatingFormCoordinates.y > 0 ? (
          <FloatingComponent
            x={floatingFormCoordinates.x}
            y={floatingFormCoordinates.y}
          >
            <AttachmentDiscussion
              activeDiscussion={activeDiscussion}
              attachmentId={compareAttachmentId}
              discussion={activeDiscussion}
              instance={instance.current}
              projectId={rightAttachment.projectId}
              setActiveAnnotation={setActiveAnnotation}
              setActiveDiscussion={setActiveDiscussion}
              setFloatingFormCoordinates={setFloatingFormCoordinates}
              onDiscussionClick={() => setActiveDiscussion(activeDiscussion)}
              showResolvedComments={showResolvedComments}
            />
          </FloatingComponent>
        ) : null}
        <WorkspaceWrapper>
          <ComparisionPreviewHeader
            latestVersionId={latestVersion?.attachmentId}
            leftAttachmentId={leftAttachment.attachmentId}
            rightAttachmentId={rightAttachment.attachmentId}
            isChatSectionComparisonVisible={isChatSectionComparisonVisible}
            setChatSectionComparisonVisiblityToggle={() =>
              setChatSectionComparisonVisiblity((value) => !value)
            }
            latestVersion={latestVersion}
            onOpenParentTask={onOpenParentTask}
          />
          <ComaparisonModeWrapper>
            <Wrapper>
              <PreviewToolbar
                instance={selectedInsatnce}
                activeTool={activeToolOverlay}
                zoom={1}
                showResolvedComments={showResolvedCommentsSelected}
                filteredDiscussions={
                  isLeftAttachment
                    ? filteredDicsussionsLeft
                    : filteredDicsussions
                }
                discussions={isLeftAttachment ? discussionsLeft : discussions}
                annotationVisiblity={annotationVisiblity}
                toggleAnnotationVisiblity={() =>
                  setAnnotationVisiblity(!annotationVisiblity)
                }
              />

              <OverlayWrapper>
                <ReactCompareSlider
                  itemOne={
                    <StyledDiv
                      ref={(_current) => {
                        refLeft.current = _current;
                      }}
                      id={WORKSPACE_ID}
                    />
                  }
                  itemTwo={
                    <StyledDiv
                      ref={(_current) => {
                        ref.current = _current;
                      }}
                      id={WORKSPACE_ID}
                    />
                  }
                  onlyHandleDraggable
                  boundsPadding={0}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </OverlayWrapper>
              <ComparisonFooter
                isOverlay
                attachmentList={versionFileList}
                leftAttachment={leftAttachment}
                setLeftAttachment={setLeftAttachment}
                rightAttachment={rightAttachment}
                setRightAttachment={setRightAttachment}
                isAttachmentLinked={isAttachmentLinked}
                toggleAttachmentLink={() =>
                  setIsAttachmentLinked((isLinked) => !isLinked)
                }
              />
            </Wrapper>
            <CommentsSection isClose={!isChatSectionComparisonVisible}>
              <ComparisonComments
                versionFileList={versionFileList}
                leftAttachment={leftAttachment}
                rightAttachment={rightAttachment}
                selectedInsatnce={selectedInsatnce}
                discussionAttachmentId={discussionAttachmentId}
                setDiscussionAttachmentId={(value) =>
                  setDiscussionAttachmentId(value)
                }
                showResolvedComments={showResolvedCommentsSelected}
                toggleShowResolvedComments={() =>
                  isLeftAttachment
                    ? setShowResolvedCommentsLeft((value) => !value)
                    : setShowResolvedComments((value) => !value)
                }
              />
            </CommentsSection>
          </ComaparisonModeWrapper>
        </WorkspaceWrapper>
      </>
    );
  }

  return (
    <LoadingContainer>
      <Loading size="large" />
    </LoadingContainer>
  );
};
